<script setup>
import {
	ref,
	computed,
	watch,
	onMounted,
} from 'vue'

import { useRoute } from 'vue-router'
import { mdiCar, mdiDelete, mdiClose } from '@mdi/js'
import { getImageService } from '@/services/image-service'
import { useInspectionStore } from '@/stores/inspection-store'
import { HTTP } from '@/services/http'
import { Notify } from '@/modules/util/notify'
import { vehicleImageUrl } from '@/modules/util/vehicle-image'
import { ImageResizeManager } from '@/modules/util/imageresizer'
import { useVehicleStore } from '@/stores/vehicle-store'
import { suggestComponent, componentPosition, hasZoneMap } from '@/modules/splat-zones'
import { damageNumbers } from '@/modules/util/damage-number'

import CameraInput from '@/components/inputs/CameraInput.vue'
import DamagePosInput from '@/components/inputs/DamagePosInput.vue'

const iStore = useInspectionStore()
const vStore = useVehicleStore()

const imageService = getImageService(iStore.currentInspection)
const route = useRoute()

const props = defineProps(['field', 'modelValue', 'splat'])
const emit = defineEmits(['update:modelValue', 'update'])

const show = ref(false)
const showOptions = ref([])
const namaGradeValues = ref([])

const splat = ref(iStore.currentInspection.splat || props.field.splat || 'splat')
const showSplat = computed(() => props.field.zone === 'Exterior' && props.field.splat !== false)

const emptyItem = {
	damagemappos: {
		x: null,
		y: null,
	},
	date: null,
	zone: props.field.zone,
	category: null,
	component: null,
	condition: null,
	severity: null,
	notes: null,
	image: null,
	imagename: null,
}

const newItem = () => {
	const item = { ...emptyItem, damagemappos: { ...emptyItem.damagemappos } }
	item.date = new Date().toLocaleString('en-GB')
	return item
}

const item = ref(newItem())
const validItem = computed(() => 
	item.value.category 
	&& item.value.component 
	&& item.value.condition 
	&& item.value.severity
)

const categoryEl = ref(null)
const componentEl = ref(null)
const conditionEl = ref(null)
const severityEl = ref(null)

const showDelete = ref(false)
const selectedDamage = ref(null)

const alternatives = ref([])

const numbers = computed(() => damageNumbers(iStore.currentDamageItems))
const itemNumber = computed(() => numbers.value.get(item.value) || numbers.value.next)
const pendingSplat = ref(null)

const isSuggested = computed(() => !!item.value.suggested
	&& item.value.suggested.category === item.value.category
	&& item.value.suggested.component === item.value.component
)

// The inspector has chosen something other than what we suggested, so leave their choice alone
const isOverridden = (target) => !!target.component
	&& !(target.suggested
		&& target.suggested.category === target.category
		&& target.suggested.component === target.component)

const setComponent = (category, component) => {
	if (item.value.category === category && item.value.component === component)
		return

	item.value.category = category
	item.value.component = component
	delete item.value.condition
	delete item.value.severity
}

const onDamagePos = (pos) => {
	item.value.damagemappos = { ...pos, splat: splat.value }

	const result = suggestComponent(splat.value, pos.x, pos.y, namaGradeValues.value)

	const otherCategory = !!item.value.category && !item.value.component
		&& !!result.suggestion && result.suggestion.category !== item.value.category

	// Keep what the inspector chose themselves, but offer what the marked area looks like
	if (isOverridden(item.value) || otherCategory) {
		if (result.suggestion?.component === item.value.component)
			item.value.suggested = result.suggestion

		alternatives.value = [...(result.suggestion ? [result.suggestion] : []), ...result.alternatives]
			.filter(a => a.component !== item.value.component)
		return
	}

	alternatives.value = result.alternatives

	if (result.suggestion) {
		item.value.suggested = result.suggestion
		setComponent(result.suggestion.category, result.suggestion.component)
	} else if (item.value.suggested) {
		delete item.value.suggested
		setComponent(null, null)
	}
}

const pickAlternative = (alt) => {
	const previous = item.value.component
		? { category: item.value.category, component: item.value.component }
		: null
	item.value.suggested = alt
	setComponent(alt.category, alt.component)
	alternatives.value = [
		...(previous ? [previous] : []),
		...alternatives.value.filter(a => a.component !== alt.component && a.component !== previous?.component),
	]
}

const alternativesLabel = computed(() => {
	if (isSuggested.value)
		return 'Or did you mean'
	return item.value.component ? 'The marked area looks like' : 'Which part is it?'
})

const updateDamageItems = async () => {
	show.value = false

	const items = [...(props.modelValue || [])]

	if (!items.includes(item.value))
		items.push(item.value)

	emit('update:modelValue', items)
	iStore.setDamageItems(items)
	item.value = newItem()
}

const positionedItems = () => (props.modelValue || [])
	.filter(d => d.zone === props.field.zone && d.damagemappos?.x && d.damagemappos?.y)

const applySplat = (v) => {
	splat.value = v
	iStore.currentInspection.splat = v
}

const updateSplat = (v) => {
	if (v === splat.value)
		return

	if (positionedItems().length) {
		pendingSplat.value = v
		return
	}

	applySplat(v)
}

// Move each existing marker onto the same component on the new diagram; clear it if there isn't one
const confirmSplatChange = () => {
	const v = pendingSplat.value
	pendingSplat.value = null

	const items = (props.modelValue || []).map(d => {
		if (d.zone !== props.field.zone || !d.damagemappos?.x)
			return d

		const pos = componentPosition(v, d.component)
		return { ...d, damagemappos: pos ? { ...pos, splat: v } : { x: null, y: null, splat: v } }
	})

	applySplat(v)
	emit('update:modelValue', items)
	iStore.setDamageItems(items)
}

const unplacedCount = computed(() => {
	if (!pendingSplat.value)
		return 0
	return positionedItems().filter(d => !componentPosition(pendingSplat.value, d.component)).length
})

const onImageTaken = async (value) => {
	try {
		const res = await imageService.uploadInspection(route.params.id, value.url)
		if (!res) {
			throw new Error('Image upload failed')
		}

		const blob = await ImageResizeManager.loadAndResizeImage(value.url, props.field.width || 100)
		const url = URL.createObjectURL(blob)
        
		item.value.image = url
		item.value.uid = res.uid
	} catch (e) {
		Notify.error('Unexpected error: ' + e.message)
		delete item.value.image
		return
	}
}

const showDeleteDialog = (damage) => {
	showDelete.value = true
	selectedDamage.value = damage
}

const deleteDamageItem = () => {
	const newVal = [...props.modelValue]
	const index = props.modelValue.findIndex(val => val === selectedDamage.value)

	if (index > -1) {
		const item = newVal.splice(index, 1)
		emit('update:modelValue', newVal)

		if (item.uid)
			HTTP.deleteCached(item.uid)
	}

	iStore.setDamageItems(newVal)

	showDelete.value = false
	selectedDamage.value = null
}

const clearItemFields = (...fields) => {
	document.activeElement.blur()
    
	fields.forEach(field => {
		delete item.value[field]
	})

	if (fields.includes('severity') && fields.length === 1) {
		const options = Object.keys(namaGradeValues.value[
			props.field.zone
		][ 	item.value.category
		][ 	item.value.component
		][ 	item.value.condition
		])

		if (options.length === 1)
			item.value.severity = options[0]
	}
}

const openDialog = (damage=null) => {
	item.value = damage || newItem()
	alternatives.value = []
	show.value = true           
}

const closeDialog = () => {
	item.value = newItem()
	alternatives.value = []
	show.value = false             
}

onMounted(async () => {
	namaGradeValues.value = iStore.allNamaGradeValues

	if (!showSplat.value)
		return

	const stop = watch(
		() => vStore.currentVehicle,
		vehicle => {
			if (!vehicle.classification_id)
				return

			stop()

			if (iStore.currentInspection.splat)
				return

			applySplat({ 1: 'splat', 2: 'splat-lcv', 3: 'splat-hgv', 7: 'splat-trailer' 
			}[vehicle.classification_id] || props.field.splat || 'splat')
		}
	)
})
</script>

<template>
<div>
	<div :class="`w-100 text-center ${field.class}`">
		<template v-if="showSplat">
			<DamagePosInput
				:model-value="item.damagemappos"
				:show-all="true"
				:splat="splat"
				:number="itemNumber"
				class="mx-auto"
				style="width: clamp(300px, 100%, 600px);"
				@update:model-value="(val) => { onDamagePos(val); show = true; }"
				@update:splat="updateSplat"
			/>
		</template>
		<v-btn
			class="w-100 my-4"
			color="primary"
			@click="() => openDialog()"
		>
			{{ field.label }}
		</v-btn>
	</div>

	<v-dialog 
		v-model="show"
		fullscreen
	>
		<v-card>
			<v-toolbar>
				<v-btn
					:icon="mdiClose"
					class="ms-auto"
					variant="plain"
					@click="closeDialog"
				/>
				<v-toolbar-title>{{ field.label }}</v-toolbar-title>
			</v-toolbar>
			
			<v-card-text>
				<DamagePosInput
					v-if="showSplat"
					:model-value="item.damagemappos"
					:splat="splat"
					:number="itemNumber"
					class="mx-auto"
					style="width: clamp(300px, 100%, 600px);"
					@update:model-value="onDamagePos"
					@update:splat="updateSplat"
				/>
				<div class="my-4 w-100">
					<div 
						v-if="item.image || item.imagename" 
						class="text-center mb-4"
					>
						<v-avatar
							size="150"
							class="mb-2"
							:image="item.image || vehicleImageUrl(item.imagename)"
						/>
						<v-btn 
							color="primary"
							class="w-100"
							@click="() => { item.image = null; item.imagename = null; item.uid = null; }"
						>
							Retake Photo
						</v-btn>
					</div>
					<CameraInput
						v-else
						class="w-100"
						variant="outlined"
						color="primary"
						@update="onImageTaken"
					>
						Take Picture
					</CameraInput>
				</div>
				<v-select
					ref="categoryEl"
					v-model="item.category"
					:items="Object.keys(namaGradeValues[field.zone])"
					label="Category"
					outlined
					dense
					clearable
					@update:model-value="clearItemFields('severity', 'condition', 'component'); alternatives = []"
				/>
				<v-select
					v-if="item.category"
					ref="componentEl"
					v-model="item.component"
					:items="Object.keys(namaGradeValues[field.zone][item.category])"
					label="Component"
					:hint="isSuggested ? 'Suggested from the damage position' : ''"
					:persistent-hint="isSuggested"
					@update:model-value="clearItemFields('severity', 'condition'); alternatives = []"
				/>
				<div
					v-if="alternatives.length"
					class="mb-4"
				>
					<div class="text-caption text-medium-emphasis mb-1">
						{{ alternativesLabel }}
					</div>
					<v-chip
						v-for="alt in alternatives"
						:key="alt.component"
						class="me-2 mb-2"
						color="primary"
						variant="outlined"
						@click="pickAlternative(alt)"
					>
						{{ alt.component }}
					</v-chip>
				</div>
				<v-select
					v-if="item.component"
					ref="conditionEl"
					v-model="item.condition"
					:items="Object.keys(namaGradeValues[field.zone][item.category][item.component])"
					label="Condition"
					@update:model-value="clearItemFields('severity')"
				/>
				<v-select
					v-if="item.condition"
					ref="severityEl"
					v-model="item.severity"
					:items="Object.keys(namaGradeValues[field.zone][item.category][item.component][item.condition])"
					label="Severity"
				/>
				<v-textarea
					v-model="item.notes"
					label="Notes"
					outlined
					dense
					clearable
					template
				/>
				<v-btn
					:disabled="!validItem"
					class="w-100 my-4"
					color="success"
					@click="updateDamageItems"
				>
					Save
				</v-btn>
			</v-card-text>
		</v-card>
	</v-dialog>

	<v-list>
		<v-list-item
			v-for="(damage, index) in (modelValue || []).filter(val => val.zone === field.zone)"
			:key="index"
			@click="() => {
				const current = showOptions[index]
				showOptions = []
				showOptions[index] = !current
			}"
		>
			<template #prepend>
				<v-avatar
					v-if="numbers.get(damage)"
					color="primary"
					class="font-weight-bold me-2"
				>
					{{ numbers.get(damage) }}
				</v-avatar>
				<v-avatar
					v-if="damage.image"
					:image="damage.image"
				/>
				<v-avatar
					v-else-if="damage.imagename"
					:image="vehicleImageUrl(damage.imagename)"
				/>
				<v-avatar
					v-else-if="!numbers.get(damage)"
					color="grey-lighten-1"
				>
					<v-icon color="white">
						{{ mdiCar }}
					</v-icon>
				</v-avatar>
			</template>
			<v-list-item-subtitle>
				{{ damage.date }}
			</v-list-item-subtitle>
			<v-list-item-title>
				{{ damage.category }} - {{ damage.component }} - {{ damage.condition }} - {{ damage.severity }}
			</v-list-item-title>
			<template #append>
				<v-btn
					v-show="!!showOptions[index]"
					color="success"
					size="small"
					style="margin-right: 8px"
					@click="openDialog(damage)"
				>
					Edit
				</v-btn>
				<v-btn
					v-show="!!showOptions[index]"
					color="error"
					size="small"
					:append-icon="mdiDelete"
					@click="showDeleteDialog(damage)"
				>
					Delete
				</v-btn>
			</template>
		</v-list-item>
	</v-list>

	<v-dialog
		:model-value="!!pendingSplat"
		@update:model-value="v => { if (!v) pendingSplat = null }"
	>
		<v-card title="Change vehicle diagram?">
			<v-card-text>
				Damage markers will move to the same part on the new diagram.
				<template v-if="!hasZoneMap(pendingSplat)">
					This diagram has no part map yet, so markers will need repositioning.
				</template>
				<template v-else-if="unplacedCount">
					{{ unplacedCount }} marker{{ unplacedCount === 1 ? '' : 's' }} can't be matched and will need repositioning.
				</template>
			</v-card-text>
			<v-card-actions>
				<v-spacer />
				<v-btn @click="pendingSplat = null">
					Cancel
				</v-btn>
				<v-btn
					color="primary"
					@click="confirmSplatChange"
				>
					Change
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>

	<v-dialog v-model="showDelete">
		<v-card title="Are you sure?">  
			<v-card-text>This action cannot be undone.</v-card-text>
			<v-card-actions>
				<v-spacer />
				<v-btn @click="showDelete = false; selectedDamage = null">
					Cancel
				</v-btn>
				<v-btn
					color="primary"
					@click="deleteDamageItem"
				>
					OK
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</div>
</template>

<style lang="scss" scoped>
</style>